import { MusicCard } from '@components/Cards';
import ContributorMediaModal from '@components/ContributorMediaModal';
import Loader from '@components/Loader';
import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { useFavorites } from '@hooks/useFavorites';
import useIndianMusicInfiniteScroll from '@hooks/useIndianMusicInfiniteScroll';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Album } from 'src/types';
import { placeHolderData } from '../../../../config';
import { getTrackUrlForLocale } from '../../../../utils/localeUtils';

interface GridPostsProps {
  isActive?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  genre?: string;
  search?: string;
}

export const IndianMusicPageGridPosts = ({
  isActive = true,
  scrollContainerRef,
  genre,
  search,
}: GridPostsProps) => {
  const { t } = useTranslation();
  const { setActivePlaylist, addRecentTrack, preferredLocale } =
    useMusicState();
  const { setActivePopup } = useCommonState();
  const { toggleFavorite } = useFavorites();
  const { loadAlbumById } = useAlbumData();
  const [loadingAlbumId, setLoadingAlbumId] = useState<string | null>(null);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    album: Album | null;
  }>({ isOpen: false, album: null });

  const handleCloseModal = useCallback(() => {
    setModalState({ isOpen: false, album: null });
  }, []);

  const { albums, loading, hasMore, error, loadMore, reset, sentinelRef } =
    useIndianMusicInfiniteScroll({
      rootMargin: '200px',
      enabled: isActive,
      scrollContainerRef,
      genre,
      search,
    });

  const openMusicPlayerPage = useCallback(
    async (album: Album) => {
      try {
        setLoadingAlbumId(album.id);
        const albumData = await loadAlbumById(album.id);

        if (!albumData || !albumData.tracks || albumData.tracks.length === 0) {
          console.warn(
            'No tracks found in album:',
            albumData?.albumName || album.id,
          );
          setLoadingAlbumId(null);
          return;
        }

        const contributor = albumData.contributors?.[0];
        const firstTrack = albumData.tracks[0];

        const newActivePlaylist = {
          currentIndex: 0,
          album: albumData,
        };

        const recentTrack = {
          track: {
            name: firstTrack.name,
            url: getTrackUrlForLocale(firstTrack, preferredLocale),
            trackIndex: 1,
          },
          contributor: {
            id: albumData.id,
            givenName: contributor?.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            albumData.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrack(recentTrack);

        setActivePlaylist(newActivePlaylist);
        setActivePopup('musicplayer');
        setLoadingAlbumId(null);
      } catch (error) {
        console.error('Error loading album:', error);
        setLoadingAlbumId(null);
      }
    },
    [
      loadAlbumById,
      setActivePlaylist,
      setActivePopup,
      addRecentTrack,
      preferredLocale,
    ],
  );

  if (loading && albums.length === 0) {
    return (
      <div id="grid-posts" className="relative w-full px-4 sm:px-6">
        <div className="responsive-grid gap-4">
          {[...Array(8)].map((_, index) => (
            <div
              key={`loading-${index}`}
              className="relative w-full h-[520px] rounded-[16px] bg-gray-800 animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)]">
              <div className="absolute top-0 left-0 w-full h-full bg-gray-700"></div>

              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,1)_100%)] z-[99] rounded-[16px]" />

              <div className="absolute top-[16px] left-[16px] z-[100] w-[78px] h-[40px] bg-gray-600 rounded-full"></div>

              <div className="absolute bottom-[68px] md:bottom-[128px] left-[20px] z-[50] overflow-hidden w-[172px] h-[132px]">
                <div className="absolute top-0 left-[35px] w-[73px] h-[73px] md:w-[132px] md:h-[132px] rounded-full bg-gray-600 z-[5]"></div>
                <div className="absolute left-0 top-0 w-[73px] h-[73px] md:w-[132px] md:h-[132px] bg-gray-600 rounded-[8px] z-[10]"></div>
              </div>

              <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-[9px] w-full">
                <div className="h-[28px] bg-gray-600 rounded w-4/5"></div>
                <div className="flex items-center gap-[16px]">
                  <div className="w-[56px] h-[56px] rounded-full bg-gray-600 flex-shrink-0"></div>
                  <div className="h-[16px] bg-gray-600 rounded w-32"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-8">
          <Loader size="medium" text={t('musicPage.loadingAlbums')} />
        </div>
      </div>
    );
  }

  if (error && albums.length === 0) {
    return (
      <div id="grid-posts" className="relative w-full px-4 sm:px-6">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-red-400 text-center mb-4">
            <svg
              className="w-12 h-12 mx-auto mb-4"
              fill="currentColor"
              viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-lg font-medium">{t('musicPage.failedToLoad')}</p>
            <p className="text-sm text-red-300 mt-1">{error}</p>
          </div>
          <button
            onClick={() => {
              reset();
            }}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200">
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!loading && !error && albums.length === 0) {
    return (
      <div id="grid-posts" className="relative w-full px-4 sm:px-6">
        <div className="flex justify-center items-center py-12">
          <div className="text-center text-white/60">
            <div className="mb-4">
              <svg
                className="w-12 h-12 mx-auto mb-4 text-white/40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                />
              </svg>
            </div>
            <p className="text-lg font-medium mb-2">
              {genre !== undefined && search
                ? t('musicPage.noContentGenreSearch', { genre, search })
                : genre !== undefined
                ? t('musicPage.noContentGenre', { genre })
                : search
                ? t('musicPage.noContentSearch', { search })
                : t('musicPage.noContentDefault')}
            </p>
            <p className="text-sm text-white/40">
              {t('musicPage.noContentHelp')}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div id="grid-posts" className="relative w-full px-4 sm:px-6">
      <div className="responsive-grid gap-4">
        {albums.map((album: Album, index: number) => {
          const contributor = album.contributors[0];
          const isLoading = loadingAlbumId === album.id;
          const posterUrl =
            album.videoMobilePreviewUrl ||
            contributor?.videoMobilePreviewUrl ||
            contributor?.thumbnailUrl;
          return (
            <div key={`album-${index}`} className="flex flex-col relative">
              <MusicCard
                album={album}
                contributor={contributor}
                videoUrl={album.videoMobileUrl || contributor?.videoMobileUrl}
                posterUrl={posterUrl}
                albumArtUrl={album.albumArtUrl}
                albumName={album.albumName}
                likes={album.likes}
                isLiked={album.isLiked}
                onClick={() => openMusicPlayerPage(album)}
                onFavoriteClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(
                    'album',
                    album.id,
                    album.isLiked,
                    album.albumName,
                    contributor?.givenName,
                    album.category,
                  );
                }}
                showPlayButton={false}
                showFavoriteButton={false}
                showLikesCount={true}
              />
              {isLoading && (
                <div className="absolute z-[100] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <NapsterSpinner />
                </div>
              )}
            </div>
          );
        })}

        {hasMore && albums.length > 0 && (
          <div
            ref={sentinelRef}
            className="col-span-full flex justify-center py-8">
            {loading ? (
              <Loader size="medium" text={t('musicPage.loadingMore')} />
            ) : (
              <div className="w-full h-4"></div>
            )}
          </div>
        )}
      </div>

      {error && albums.length > 0 && (
        <div className="flex justify-center mt-4">
          <div className="text-red-400 text-center">
            <p className="text-sm">{t('musicPage.failedToLoadMore')}</p>
            <button
              onClick={() => {
                loadMore();
              }}
              className="mt-2 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors duration-200">
              {t('common.tryAgain')}
            </button>
          </div>
        </div>
      )}

      {modalState.isOpen && modalState.album && (
        <ContributorMediaModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          contributor={modalState.album.contributors[0]}
          album={modalState.album}
        />
      )}
    </div>
  );
};
